import { z } from 'zod';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { ApiError } from './api';

/**
 * Cliente HTTP + hooks para los agregados ricos del dashboard (M2.12).
 *
 * Endpoints:
 *  - GET /api/stats/by-discipline?days=N     → métricas por disciplina con
 *    comparativa contra el periodo anterior (mismo nº de días).
 *  - GET /api/stats/activity-heatmap?days=N  → array densoo de días con TSS +
 *    count de actividades (estilo GitHub contributions).
 *
 * Sigue el mismo patrón que `lib/load.ts`: schemas Zod, fetch propio.
 */

const API_BASE_URL: string = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/$/, '');

// ---------------------------------------------------------------------------
// Schemas
// ---------------------------------------------------------------------------

export const DISCIPLINE_STATUSES = ['high', 'normal', 'low', 'none'] as const;
export type DisciplineStatus = (typeof DISCIPLINE_STATUSES)[number];

export const DisciplineStatsSchema = z.object({
  discipline: z.string(),
  sessions: z.number().int().nonnegative(),
  hours: z.number().nonnegative(),
  distanceKm: z.number().nonnegative(),
  prevSessions: z.number().int().nonnegative(),
  prevHours: z.number().nonnegative(),
  prev30dRatio: z.number().nullable(),
  status: z.enum(DISCIPLINE_STATUSES),
});
export type DisciplineStats = z.infer<typeof DisciplineStatsSchema>;

export const ByDisciplineResponseSchema = z.object({
  days: z.number().int().positive(),
  disciplines: z.array(DisciplineStatsSchema),
});
export type ByDisciplineResponse = z.infer<typeof ByDisciplineResponseSchema>;

export const HeatmapDaySchema = z.object({
  dateISO: z.string(),
  tss: z.number().nonnegative(),
  count: z.number().int().nonnegative(),
});
export type HeatmapDay = z.infer<typeof HeatmapDaySchema>;

export const ActivityHeatmapResponseSchema = z.object({
  days: z.array(HeatmapDaySchema),
});
export type ActivityHeatmapResponse = z.infer<typeof ActivityHeatmapResponseSchema>;

// ---------------------------------------------------------------------------
// HTTP helpers (gemelos a lib/load.ts)
// ---------------------------------------------------------------------------

async function readJsonSafe(response: Response): Promise<unknown> {
  try {
    return await response.json();
  } catch {
    return null;
  }
}

async function getJson<T>(path: string, schema: z.ZodType<T>): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}${path}`, {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
    });
  } catch (err) {
    throw new ApiError('Error de red al contactar con el backend', 0, err);
  }
  if (!response.ok) {
    throw new ApiError(`Respuesta no OK del backend: ${response.status}`, response.status);
  }
  const json = await readJsonSafe(response);
  const parsed = schema.safeParse(json);
  if (!parsed.success) {
    throw new ApiError(`Respuesta ${path} no cumple esquema`, response.status, parsed.error);
  }
  return parsed.data;
}

// ---------------------------------------------------------------------------
// Endpoints
// ---------------------------------------------------------------------------

export function getByDiscipline(days = 30): Promise<ByDisciplineResponse> {
  return getJson(
    `/api/stats/by-discipline?days=${encodeURIComponent(String(days))}`,
    ByDisciplineResponseSchema,
  );
}

export function getActivityHeatmap(days = 180): Promise<ActivityHeatmapResponse> {
  return getJson(
    `/api/stats/activity-heatmap?days=${encodeURIComponent(String(days))}`,
    ActivityHeatmapResponseSchema,
  );
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

export const STATS_QUERY_KEY = ['stats'] as const;
export const STATS_BY_DISCIPLINE_KEY = ['stats', 'by-discipline'] as const;
export const STATS_HEATMAP_KEY = ['stats', 'activity-heatmap'] as const;

export function useStatsByDiscipline(
  days = 30,
): UseQueryResult<ByDisciplineResponse, Error> {
  return useQuery<ByDisciplineResponse, Error>({
    queryKey: [...STATS_BY_DISCIPLINE_KEY, days],
    queryFn: () => getByDiscipline(days),
    staleTime: 30_000,
    retry: 1,
  });
}

export function useActivityHeatmap(
  days = 180,
): UseQueryResult<ActivityHeatmapResponse, Error> {
  return useQuery<ActivityHeatmapResponse, Error>({
    queryKey: [...STATS_HEATMAP_KEY, days],
    queryFn: () => getActivityHeatmap(days),
    staleTime: 60_000,
    retry: 1,
  });
}
